import { useState } from 'react'
import ReactDOM from 'react-dom'
import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { BigNumber } from 'bignumber.js'

// Libs
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'
import useMedia from '@/hooks/useMedia'
import { useIsCSR } from '@/hooks/useIsCSR'
import { handleGtmClickHost } from '@/methods/handleGtmEvent'

// Components
import Image from '@/components/common/Image'
import Drawer from '@/components/common/Drawer'
import Insight from '@/components/shared/Insight'

// Constants
import { ROUTE_PATH } from '@/constants/routePath'
import { GTM_LOCATION, GtmClickLiveRoomPosition } from '@/constants/gtmEvent'
import { VALID_BRAND_NAME } from '@/constants/brand'

// Types
import { MerchantDataType } from '@/types/room'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

const User = dynamic(() => import('@/components/pages/User'), { ssr: false })

// interface MerchantProps {}

interface GtmParams {
  gtmClickLiveRoomPosition?: GtmClickLiveRoomPosition
}

const Merchant = (props: MerchantDataType & GtmParams) => {
  const { kolHeadshotUrl, kolName, dummyViews, hostMemberInfoId, gtmClickLiveRoomPosition } = props

  const { t } = useTranslation()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const router = useRouter()

  const { isMobile } = useMedia()
  const { isCSR } = useIsCSR()
  const brandName: any = router?.query?.brandName || ''
  const isValidBrandName = Object.values(VALID_BRAND_NAME).includes(brandName)

  const handleMerchantClick = () => {
    if (isValidBrandName) return

    handleGtmClickHost({ hostId: hostMemberInfoId as string, location: GTM_LOCATION.LIVE_ROOM })
    if (isMobile) {
      setIsDrawerOpen(true)
    } else {
      handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.USER}/${hostMemberInfoId}` })
    }
  }

  const drawerContainer: any = typeof window === 'undefined' ? null : document.getElementById('room')

  return (
    <>
      <div className={cx('merchant', { '--no-event': isValidBrandName })} onClick={handleMerchantClick}>
        <div className={cx('merchant-image-left')}>
          <div className={cx('merchant-image')}>
            <Image src={kolHeadshotUrl} width={36} height={36} style={{ objectFit: 'cover' }} alt='kol avatar' />
          </div>
          <Insight
            className={cx('merchant__live-status')}
            status='empty'
            theme='gray'
            position='absolute'
            bottom='0px'
            right='8px'
            gtmClickLiveRoomPosition={gtmClickLiveRoomPosition}
          />
        </div>
        <div className={cx('merchant-content')}>
          <p className={cx('merchant-content-name')}>{kolName}</p>
          <div className={cx('merchant-content-views')}>
            <span className={cx('merchant-content-views-count')}>{t('Common_Views', { views: new BigNumber(dummyViews).toFormat() })}</span>
          </div>
        </div>
      </div>

      {/* #room 只有直播間版面才有；在別處（例如第三方內嵌頁）用到 Merchant 時容器會是 null，
          createPortal 傳 null 會直接拋錯，所以要先確認容器存在 */}
      {isCSR &&
        drawerContainer &&
        ReactDOM.createPortal(
          <Drawer
            className={cx('merchant-detail')}
            open={isDrawerOpen && isMobile}
            direction='bottom'
            onClose={() => setIsDrawerOpen(false)}
            lockBackgroundScroll
            style={{ height: '100%', padding: '20px 0px' }}
          >
            <User memberInfoId={hostMemberInfoId} isDrawer />
          </Drawer>,
          drawerContainer,
        )}
    </>
  )
}

export default Merchant
